import { useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';

const useTelegramWebApp = () => {
  const { 
    setLoading,
    validateAndSetUser,
    loadUserSubscriptions,
    setTelegramData,
    setTheme,
    telegramTheme,
    isTelegramAvailable,
    error,
    hasActiveLoading,
    isAuthenticated
  } = useApp();

  // Используем ref для отслеживания состояния инициализации
  const initializationRef = useRef({
    isInitialized: false,
    isInitializing: false
  });

  // Мемоизируем функции для предотвращения лишних ре-рендеров
  const initializeTelegramWebApp = useCallback(async () => {
    // Предотвращаем повторную инициализацию
    if (initializationRef.current.isInitialized || initializationRef.current.isInitializing) {
      return;
    }

    initializationRef.current.isInitializing = true;

    try {
      setLoading(true);
      
      // Ждем загрузки скрипта Telegram
      await new Promise((resolve) => {
        if (window.Telegram?.WebApp) {
          resolve();
        } else {
          const checkTelegram = setInterval(() => {
            if (window.Telegram?.WebApp) {
              clearInterval(checkTelegram);
              resolve();
            }
          }, 100);
          
          // Timeout после 5 секунд
          setTimeout(() => {
            clearInterval(checkTelegram);
            resolve();
          }, 5000);
        }
      });

      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        
        // Включаем fullscreen режим
        tg.expand();
        if (tg.platform == 'android' || tg.platform == 'ios') {
          tg.requestFullscreen();
        }
        tg.disableVerticalSwipes();
        tg.lockOrientation('portrait');
        
        // Получаем тему из Telegram
        const themeParams = tg.themeParams;
        
        // Определяем тему на основе цветовой схемы Telegram (для использования в качестве fallback)
        const telegramTheme = themeParams.bg_color && 
          (themeParams.bg_color.includes('#1') || themeParams.bg_color.includes('#0')) ? 'dark' : 'light';
        
        // Настраиваем заголовок и фон
        if (themeParams.bg_color) {
          tg.headerColor = themeParams.header_bg_color || themeParams.bg_color;
          tg.backgroundColor = themeParams.bg_color;
        }
        
        // Устанавливаем данные Telegram в контекст
        setTelegramData(true, themeParams);
        
        // Делаем приложение готовым
        tg.setBackgroundColor(telegramTheme === 'dark' ? '#1d1d1d' : '#f5f5f5');
        tg.ready();
        
        // Получаем данные пользователя и отправляем на валидацию через контекст
        const user = tg.initDataUnsafe?.user || tg.user;
        if (user && user.id) {
          // Получаем start параметр из Telegram WebApp или URL параметров
          let startParam = tg.initDataUnsafe?.start_param || null;
          
          // Если start параметр не найден в Telegram данных, проверяем URL параметры
          if (!startParam) {
            const urlParams = new URLSearchParams(window.location.search);
            startParam = urlParams.get('start_param');
          }
          
          const userData = {
            user_id: user.id,
            first_name: user.first_name || null,
            username: user.username || null,
            photo_url: user.photo_url || null,
            start_param: startParam  // Добавляем start параметр для реферальной программы
          };
          
          console.log('Данные пользователя для валидации:', userData);
          
          try {
            // Используем метод контекста для валидации пользователя
            const validatedUser = await validateAndSetUser(userData, telegramTheme);
            
            // После успешной валидации загружаем подписки пользователя
            if (validatedUser && validatedUser.id) {
              try {
                await loadUserSubscriptions(validatedUser.id);
              } catch (subscriptionsError) {
                console.error('Не удалось загрузить подписки пользователя:', subscriptionsError);
                // Продолжаем работу приложения даже если подписки не загрузились
              }
            }
            
            // Если у пользователя нет сохраненной темы в БД, применяем тему из Telegram
            // Это будет проверено в validateAndSetUser - если theme_mode не пришел, применим Telegram тему
          } catch (validationError) {
            console.error('Не удалось провалидировать пользователя:', validationError);
            // В случае ошибки валидации применяем тему из Telegram
            setTheme(telegramTheme);
          }
        } else {
          console.warn('Данные пользователя не найдены в Telegram WebApp');
          // Устанавливаем демо пользователя для разработки и применяем тему из Telegram
          try {
            throw new Error('Данные пользователя не найдены в Telegram WebApp');
          } catch (demoError) {
            console.error('Ошибка создания демо пользователя:', demoError);
          }
        }
        
      } else {
        setTelegramData(false, {});
        throw new Error('Данные пользователя не найдены в Telegram WebApp');
        
      }

      // Помечаем инициализацию как завершенную
      initializationRef.current.isInitialized = true;
    } catch (err) {
      console.error('Ошибка инициализации Telegram WebApp:', err);
      setTelegramData(false, {});
      throw new Error('Данные пользователя не найдены в Telegram WebApp');
      
    } finally {
      initializationRef.current.isInitializing = false;
      setLoading(false);
    }
  }, [setLoading, validateAndSetUser, loadUserSubscriptions, setTelegramData, setTheme]);

  useEffect(() => {
    // Инициализируем только если еще не инициализирован и пользователь не аутентифицирован
    if (!initializationRef.current.isInitialized && !isAuthenticated && !initializationRef.current.isInitializing) {
      initializeTelegramWebApp();
    }
  }, []); // Пустой массив зависимостей - инициализация только при монтировании

  return {
    telegramTheme,
    isLoading: hasActiveLoading,
    error,
    isTelegramAvailable
  };
};

export default useTelegramWebApp; 